// src/settings/ProfileSettings.jsx
'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { updateUserProfile } from '@/services/userService';
import { updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db, storage, app } from '@/firebase/firebase';
import { ref, deleteObject } from 'firebase/storage';
import {
  doc, onSnapshot, serverTimestamp, updateDoc, setDoc, deleteField,
  collection, query, where, orderBy, limit, getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';

import {
  SMS_CONSENT_TEXT,
  SMS_CONSENT_VERSION,
  CONSENT_SOURCES,
} from '@/constants/consent';
import { LEGAL_URLS } from '@/constants/legal';
import { Field, Label as CatalystLabel, Description } from '@/components/fieldset';
import {
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  UserCircleIcon,
  KeyIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import SquareLogoUploader from '@/components/inputs/SquareLogoUploader.jsx';
import { uploadBlobToStorage } from '@/services/storageService';
import { Avatar } from '@/components/avatar';
import LanguageSettingsCard from '@/components/settings/LanguageSettingsCard';
import MessageToneSelector from '@/components/MessageToneSelector';
import clsx from 'clsx';

/* ---------- Lime/pitch design tokens ---------- */
const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ---------- Helpers ---------- */
function getDeletableStorageRef(url) {
  try {
    if (!url) return null;
    const isGS = url.startsWith('gs://');
    const isFirebaseHttps = /^https:\/\/firebasestorage\.googleapis\.com\//i.test(url);
    if (isGS || isFirebaseHttps) return ref(storage, url);
    return null;
  } catch {
    return null;
  }
}

function SectionLabel({ children }) {
  return (
    <div
      className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ children, className = '', disabled, ...props }) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all',
        'bg-[#b5f03d] hover:bg-[#94cc28]',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#b5f03d]',
        className
      )}
      style={{ fontFamily: HEAD_FONT }}
      {...props}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, className = '', ...props }) {
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-bold uppercase tracking-wide text-zinc-700 transition-all',
        'hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 hover:text-[#0d1f14]',
        className
      )}
      style={{ fontFamily: HEAD_FONT }}
      {...props}
    >
      {children}
    </button>
  );
}

const INPUT_CLASSES =
  'block w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] placeholder:text-zinc-400 transition-all focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-500';
const INPUT_INVALID =
  'border-red-300 focus:border-red-400 focus:ring-red-200';

function SectionCard({ children, title, icon: Icon, description }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="border-b border-zinc-100 px-6 py-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]">
          {Icon && <Icon className="w-5 h-5" />}
        </div>
        <div>
          <h3
            className="text-base font-bold uppercase tracking-wide text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {title}
          </h3>
          {description && <p className="text-xs text-zinc-500">{description}</p>}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

/* ---------- E.164 helpers ---------- */
// Twilio Verify expects E.164 (e.g. +14155551234). We accept loose US input
// and normalize. For non-US numbers, the user must include the leading +.
function normalizeToE164(raw) {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15 ? `+${digits}` : '';
  }
  // Assume US — strip non-digits, require 10 digits
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return '';
}

function formatPhoneDisplay(e164) {
  if (!e164) return '';
  // US-friendly display for +1 numbers
  if (e164.startsWith('+1') && e164.length === 12) {
    const d = e164.slice(2);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return e164;
}

/* ---------- Main Component ---------- */
export default function ProfileSettings() {
  const { firebaseUser } = useAuth();
  const { t } = useTranslation();

  const [form, setForm] = useState({ displayName: '' });
  const [initialPhotoURL, setInitialPhotoURL] = useState('');
  const [photoPreview, setPhotoPreview]     = useState(null);
  const [removePhotoFlag, setRemovePhotoFlag] = useState(false);
  const [processedPhoto, setProcessedPhoto]   = useState(null);
  const [errors, setErrors]   = useState({});
  const [status, setStatus]   = useState('loading');
  // Last name persisted to Firestore — lets the on-blur auto-save skip a
  // write when the field hasn't actually changed.
  const savedNameRef = useRef('');

  // Phone verification state
  const [verifiedPhone, setVerifiedPhone] = useState(''); // value persisted on user doc
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [phoneStep, setPhoneStep] = useState('idle'); // 'idle' | 'sending' | 'awaitingCode' | 'verifying'
  // Phone we found on the user's most recent season-registration parent
  // contact form. Pre-filled into the phoneInput so parents who already
  // typed their number don't have to type it again. Empty if no match.
  const [suggestedPhone, setSuggestedPhone] = useState('');

  // Load from Firestore
  useEffect(() => {
    if (!firebaseUser?.uid) return;

    const unsub = onSnapshot(doc(db, 'users', firebaseUser.uid), (snap) => {
      const data = snap.data() || {};
      const serverPhoto = data.photoURL ?? firebaseUser.photoURL ?? '';

      const serverName = data.displayName || firebaseUser.displayName || '';
      savedNameRef.current = serverName;
      setForm({ displayName: serverName });
      setInitialPhotoURL(serverPhoto);
      setPhotoPreview((cur) => {
        if (removePhotoFlag || processedPhoto) return cur;
        return serverPhoto;
      });
      const phone = data.phoneNumber || data?.phone?.phoneNumber || '';
      setVerifiedPhone(phone);
      setStatus('idle');
    });

    return unsub;
  }, [firebaseUser, removePhotoFlag, processedPhoto]);

  // Bridge the registration ↔ user data silo: if this user has previously
  // typed a phone into a season-registration parent contact form, surface
  // it as a one-tap pre-fill so they don't have to re-type. They still
  // need to verify (Twilio Verify), so this is purely a convenience.
  useEffect(() => {
    if (!firebaseUser?.email) return;
    if (verifiedPhone) return; // already verified — no need to suggest
    let cancelled = false;
    (async () => {
      try {
        const email = String(firebaseUser.email).trim().toLowerCase();
        const q = query(
          collection(db, 'registrations'),
          where('parentEmail', '==', email),
          orderBy('createdAt', 'desc'),
          limit(1),
        );
        const snap = await getDocs(q);
        if (cancelled || snap.empty) return;
        const raw = snap.docs[0].data()?.parentPhone;
        const e164 = normalizeToE164(raw);
        if (!e164) return;
        setSuggestedPhone(e164);
        // Only pre-fill if the user hasn't already typed something
        setPhoneInput((cur) => cur ? cur : formatPhoneDisplay(e164));
      } catch (err) {
        // Most likely a missing composite index. Non-blocking; the user
        // can still type the phone manually.
        if (import.meta.env.DEV) console.warn('[ProfileSettings] phone-suggest lookup failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [firebaseUser?.email, verifiedPhone]);

  // --- Phone verification handlers ---
  const handleSendCode = async () => {
    const phoneNumber = normalizeToE164(phoneInput);
    if (!phoneNumber) {
      toast.error(t('profileSettings.toast.phoneInvalid'));
      return;
    }
    setPhoneStep('sending');
    const tid = toast.loading(t('profileSettings.toast.sendingCode'));
    try {
      const fn = httpsCallable(getFunctions(app, 'us-central1'), 'startPhoneVerification');
      await fn({ phoneNumber });
      setPhoneStep('awaitingCode');
      toast.success(t('profileSettings.toast.codeSent', { phone: formatPhoneDisplay(phoneNumber) }), { id: tid });
    } catch (err) {
      console.error('[startPhoneVerification]', err);
      toast.error(err?.message || t('profileSettings.toast.sendCodeFailed'), { id: tid });
      setPhoneStep('idle');
    }
  };

  const handleVerifyCode = async () => {
    const phoneNumber = normalizeToE164(phoneInput);
    const code = codeInput.trim();
    if (!phoneNumber || !code) {
      toast.error(t('profileSettings.toast.enterCode'));
      return;
    }
    setPhoneStep('verifying');
    const tid = toast.loading(t('profileSettings.toast.verifying'));
    try {
      const fn = httpsCallable(getFunctions(app, 'us-central1'), 'checkPhoneVerification');
      const { data } = await fn({ phoneNumber, code });
      if (!data?.success) {
        toast.error(t('profileSettings.toast.invalidCode'), { id: tid });
        setPhoneStep('awaitingCode');
        return;
      }
      // Auto-enable Master SMS (the verify dialog showed the TCPA disclosure;
      // tapping Verify IS the opt-in event). We do three things atomically-ish:
      //   1. Update users/{uid}: phone fields + trust + verified phone tag
      //   2. Update notificationSettings/default: smsOptIn=true
      //   3. Append a compliance audit record via recordConsent CF — captures
      //      IP, user-agent, source, and the exact consent text shown.
      //
      // The audit record is the LEGAL source of truth (TCPA defense). The
      // user-doc compliance.* fields are a fast-read mirror that recordConsent
      // also updates server-side.
      await Promise.all([
        updateDoc(doc(db, 'users', firebaseUser.uid), {
          phoneNumber,
          phoneVerified: true,
          'trust.phoneVerified': true,
          updatedAt: serverTimestamp(),
        }),
        setDoc(
          doc(db, `users/${firebaseUser.uid}/notificationSettings/default`),
          { smsOptIn: true, updatedAt: serverTimestamp() },
          { merge: true }
        ),
      ]);
      // Audit record (best-effort; verify itself succeeded). We don't
      // block the success toast on this — if the consent log fails, the
      // user is still verified and the phoneVerified flag is set; we'll
      // log the error and a follow-up backfill can pick it up.
      try {
        const recordConsent = httpsCallable(getFunctions(app, 'us-central1'), 'recordConsent');
        await recordConsent({
          type: 'sms',
          action: 'granted',
          phoneNumber,
          source: CONSENT_SOURCES.PROFILE_VERIFY,
          consentTextVersion: SMS_CONSENT_VERSION,
          consentText: SMS_CONSENT_TEXT,
          verificationChannel: 'twilio_verify',
          identitySnapshot: {
            displayName: form.displayName || firebaseUser.displayName || null,
            email: firebaseUser.email || null,
          },
        });
      } catch (logErr) {
        console.warn('[ProfileSettings] consent log failed (non-blocking)', logErr);
      }
      setVerifiedPhone(phoneNumber);
      setPhoneInput('');
      setCodeInput('');
      setPhoneStep('idle');
      toast.success(t('profileSettings.toast.phoneVerified'), { id: tid });
    } catch (err) {
      console.error('[checkPhoneVerification]', err);
      toast.error(err?.message || t('profileSettings.toast.verifyFailed'), { id: tid });
      setPhoneStep('awaitingCode');
    }
  };

  const handleRemovePhone = async () => {
    if (!firebaseUser?.uid) return;
    if (!window.confirm(t('profileSettings.phone.confirmRemove'))) return;
    const tid = toast.loading(t('profileSettings.toast.removingPhone'));
    const removingPhone = verifiedPhone; // capture before we clear it
    try {
      await Promise.all([
        updateDoc(doc(db, 'users', firebaseUser.uid), {
          phoneNumber: deleteField(),
          phoneVerified: false,
          'trust.phoneVerified': false,
          updatedAt: serverTimestamp(),
        }),
        setDoc(
          doc(db, `users/${firebaseUser.uid}/notificationSettings/default`),
          { smsOptIn: false, updatedAt: serverTimestamp() },
          { merge: true }
        ),
      ]);
      // Log the revocation in the audit trail (TCPA requires retention of
      // opt-out events too, not just opt-ins).
      try {
        const recordConsent = httpsCallable(getFunctions(app, 'us-central1'), 'recordConsent');
        await recordConsent({
          type: 'sms',
          action: 'revoked',
          phoneNumber: removingPhone || null,
          source: CONSENT_SOURCES.PROFILE_REMOVE_PHONE,
          identitySnapshot: {
            displayName: form.displayName || firebaseUser.displayName || null,
            email: firebaseUser.email || null,
          },
        });
      } catch (logErr) {
        console.warn('[ProfileSettings] revocation log failed (non-blocking)', logErr);
      }
      setVerifiedPhone('');
      toast.success(t('profileSettings.toast.phoneRemoved'), { id: tid });
    } catch (err) {
      console.error(err);
      toast.error(t('profileSettings.toast.removePhoneFailed'), { id: tid });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: null }));
  };

  // Auto-save name + photo. Name saves on blur (when changed); photo saves
  // the moment it's processed or removed. Photo handlers pass their fresh
  // value via `overrides` so we never read stale React state mid-update.
  const commitProfile = async (overrides = {}) => {
    const displayName = String(overrides.displayName ?? form.displayName).trim();
    const processed   = 'processedPhoto' in overrides ? overrides.processedPhoto : processedPhoto;
    const removing    = 'removePhoto'    in overrides ? overrides.removePhoto    : removePhotoFlag;

    if (!displayName) { setErrors({ displayName: t('profileSettings.identity.nameRequired') }); return; }
    if (!firebaseUser) { toast.error(t('profileSettings.toast.notLoggedIn')); return; }
    setErrors({});

    setStatus('saving');
    const tid = toast.loading(t('profileSettings.toast.savingProfile'));
    let newPhotoURL = initialPhotoURL;

    try {
      const deletableRef = getDeletableStorageRef(initialPhotoURL);

      if (removing) {
        if (deletableRef) await deleteObject(deletableRef).catch(() => {});
        newPhotoURL = '';
      }
      if (processed?.blob) {
        if (deletableRef && !removing) await deleteObject(deletableRef).catch(() => {});
        const path = `profilePhotos/${firebaseUser.uid}/avatar_${Date.now()}.webp`;
        const { downloadURL } = await uploadBlobToStorage(
          processed.blob, path, { contentType: processed.blob.type }
        );
        newPhotoURL = downloadURL;
      }

      await updateUserProfile(firebaseUser.uid, {
        displayName,
        photoURL:    newPhotoURL,
        updatedAt:   serverTimestamp(),
      });

      // Keep Firebase Auth in sync
      await updateProfile(firebaseUser, {
        displayName,
        photoURL:    newPhotoURL || '',
      });

      setProcessedPhoto(null);
      setRemovePhotoFlag(false);
      setInitialPhotoURL(newPhotoURL);
      savedNameRef.current = displayName;
      setStatus('idle');
      toast.success(t('profileSettings.toast.profileSaved'), { id: tid });
    } catch (err) {
      console.error(err);
      toast.error(t('profileSettings.toast.saveFailed'), { id: tid });
      setStatus('idle');
    }
  };

  // Persist the name when the field loses focus — only if it actually
  // changed and isn't empty (empty just surfaces the inline error).
  const handleNameBlur = () => {
    const next = form.displayName.trim();
    if (!next) { setErrors({ displayName: t('profileSettings.identity.nameRequired') }); return; }
    if (next === savedNameRef.current) return;
    commitProfile({ displayName: next });
  };

  const handleRemovePhoto = () => {
    setRemovePhotoFlag(true);
    setProcessedPhoto(null);
    setPhotoPreview(null);
    commitProfile({ processedPhoto: null, removePhoto: true });
  };

  // --- Account deletion ---
  // Required by Apple Guideline 5.1.1(v), Play Data Deletion policy,
  // GDPR Art. 17, CCPA. Two-step confirmation: typed phrase + final
  // dialog. Backend handles tombstoning, Stream cleanup, Firebase Auth
  // deletion. Consent + payment records are retained per legal retention.
  const [deleteOpen, setDeleteOpen]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting]           = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE-MY-ACCOUNT') {
      toast.error(t('profileSettings.toast.typeExact'));
      return;
    }
    setDeleting(true);
    const tid = toast.loading(t('profileSettings.toast.deletingAccount'));
    try {
      const fn = httpsCallable(getFunctions(app, 'us-central1'), 'deleteMyAccount');
      await fn({ confirm: 'DELETE-MY-ACCOUNT' });
      toast.success(t('profileSettings.toast.accountDeleted'), { id: tid });
      // Sign-out is implicit (Firebase Auth user is gone). Navigate home.
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    } catch (err) {
      console.error('[deleteMyAccount]', err);
      toast.error(err?.message || t('profileSettings.toast.deleteFailed'), { id: tid });
      setDeleting(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!firebaseUser?.email) { toast.error(t('profileSettings.toast.noEmail')); return; }
    const tid = toast.loading(t('profileSettings.toast.sendingReset'));
    try {
      await sendPasswordResetEmail(auth, firebaseUser.email);
      toast.success(t('profileSettings.toast.resetSent'), { id: tid });
    } catch (err) {
      toast.error(t('profileSettings.toast.resetFailed'), { id: tid });
    }
  };

  if (status === 'loading') {
    return (
      <div className="mx-auto max-w-2xl space-y-6 animate-pulse pt-2">
        <div className="h-8 w-48 bg-zinc-200 rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-44 bg-zinc-100 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 animate-in fade-in duration-500 pt-2">

      {/* Header */}
      <div>
        <SectionLabel>{t('profileSettings.eyebrow')}</SectionLabel>
        <h2
          className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {t('profileSettings.title')}
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          {t('profileSettings.subtitle')}
        </p>
      </div>

      {/* ── 1. Identity ── */}
      <SectionCard title={t('profileSettings.identity.title')} icon={UserCircleIcon} description={t('profileSettings.identity.desc')}>
        <div className="space-y-6">
          {/* Photo */}
          <div className="flex items-start gap-5">
            <Avatar
              src={photoPreview || initialPhotoURL}
              alt="Avatar"
              className="!h-20 !w-20 flex-shrink-0 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700"
              initials={(form.displayName || firebaseUser?.email || '?')[0].toUpperCase()}
              square
            />
            <div className="flex-1 space-y-2">
              <SquareLogoUploader
                label={t('profileSettings.identity.changePhoto')}
                initialPreview={photoPreview}
                maxSize={512}
                mimeType="image/webp"
                quality={0.9}
                showPreview={false}
                bordered
                caption={null}
                onProcessed={(res) => {
                  setProcessedPhoto(res);
                  setPhotoPreview(res.dataUrl);
                  setRemovePhotoFlag(false);
                  commitProfile({ processedPhoto: res, removePhoto: false });
                }}
              />
              {(initialPhotoURL || photoPreview) && !removePhotoFlag && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                  {t('profileSettings.identity.removePhoto')}
                </button>
              )}
              <p className="text-xs text-zinc-400">{t('profileSettings.identity.photoHint')}</p>
            </div>
          </div>

          {/* Display Name */}
          <Field>
            <CatalystLabel className="mb-1.5 block text-sm font-medium text-zinc-700">
              {t('profileSettings.identity.nameLabel')}
            </CatalystLabel>
            <input
              name="displayName"
              value={form.displayName}
              onChange={handleChange}
              onBlur={handleNameBlur}
              placeholder={t('profileSettings.identity.namePlaceholder')}
              className={clsx(INPUT_CLASSES, errors.displayName && INPUT_INVALID)}
            />
            {errors.displayName && (
              <p className="mt-1 text-xs text-red-600">{errors.displayName}</p>
            )}
            <Description className="mt-1">{t('profileSettings.identity.nameDesc')}</Description>
          </Field>
        </div>
      </SectionCard>

      {/* ── Phone Number ── */}
      <SectionCard
        title={t('profileSettings.phone.title')}
        icon={DevicePhoneMobileIcon}
        description={t('profileSettings.phone.desc')}
      >
        {verifiedPhone ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0d1f14] truncate">
                    {formatPhoneDisplay(verifiedPhone)}
                  </p>
                  <p className="text-xs text-zinc-500">{t('profileSettings.phone.verifiedAvailable')}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemovePhone}
                className="flex shrink-0 items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 transition-colors"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                {t('profileSettings.phone.remove')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Field>
              <CatalystLabel className="mb-1.5 block text-sm font-medium text-zinc-700">
                {t('profileSettings.phone.mobileLabel')}
              </CatalystLabel>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder={t('profileSettings.phone.mobilePlaceholder')}
                disabled={phoneStep === 'sending' || phoneStep === 'awaitingCode' || phoneStep === 'verifying'}
                className={INPUT_CLASSES}
              />
              <Description className="mt-1">
                {suggestedPhone ? (
                  <>
                    <span className="text-[#5a8a1f] font-medium">{t('profileSettings.phone.fromRegistration')}</span>
                    {t('profileSettings.phone.fromRegistrationRest')}
                  </>
                ) : (
                  <>{t('profileSettings.phone.descDefault')}</>
                )}
              </Description>
            </Field>

            {phoneStep !== 'awaitingCode' && phoneStep !== 'verifying' && (
              <>
                {/* TCPA / 10DLC consent disclosure — text rendered from
                    SMS_CONSENT_TEXT constant so the audit log captures
                    EXACTLY what the user saw. Bump SMS_CONSENT_VERSION
                    in src/constants/consent.js whenever the wording
                    changes. The recordConsent function snapshots both. */}
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600 leading-relaxed">
                  {SMS_CONSENT_TEXT}
                </div>
                <p className="text-[11px] text-zinc-500">
                  {t('profileSettings.phone.seeFull')}{' '}
                  <a
                    href={LEGAL_URLS.SMS}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#5a8a1f] hover:text-[#3d6010] underline"
                  >
                    {t('profileSettings.phone.smsPolicy')}
                  </a>
                  {' '}{t('profileSettings.phone.and')}{' '}
                  <a
                    href={LEGAL_URLS.PRIVACY}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#5a8a1f] hover:text-[#3d6010] underline"
                  >
                    {t('profileSettings.phone.privacyPolicy')}
                  </a>.
                </p>
                <PrimaryButton
                  onClick={handleSendCode}
                  disabled={phoneStep === 'sending' || !phoneInput.trim()}
                  className="w-full sm:w-auto"
                >
                  {phoneStep === 'sending' ? (
                    <span className="flex items-center gap-2">
                      <ArrowPathIcon className="animate-spin w-4 h-4" /> {t('profileSettings.phone.sending')}
                    </span>
                  ) : t('profileSettings.phone.sendCode')}
                </PrimaryButton>
              </>
            )}

            {(phoneStep === 'awaitingCode' || phoneStep === 'verifying') && (
              <div className="space-y-3 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/5 p-4">
                <p className="text-xs text-zinc-600">
                  {t('profileSettings.phone.codeSentPrefix')}<strong className="text-[#0d1f14]">{formatPhoneDisplay(normalizeToE164(phoneInput))}</strong>{t('profileSettings.phone.codeSentSuffix')}
                </p>
                <Field>
                  <CatalystLabel className="mb-1.5 block text-sm font-medium text-zinc-700">
                    {t('profileSettings.phone.codeLabel')}
                  </CatalystLabel>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('profileSettings.phone.codePlaceholder')}
                    maxLength={6}
                    className={INPUT_CLASSES}
                  />
                </Field>
                <div className="flex flex-wrap gap-2">
                  <PrimaryButton
                    onClick={handleVerifyCode}
                    disabled={phoneStep === 'verifying' || codeInput.length < 6}
                  >
                    {phoneStep === 'verifying' ? (
                      <span className="flex items-center gap-2">
                        <ArrowPathIcon className="animate-spin w-4 h-4" /> {t('profileSettings.phone.verifying')}
                      </span>
                    ) : t('profileSettings.phone.verify')}
                  </PrimaryButton>
                  <GhostButton
                    type="button"
                    onClick={() => { setPhoneStep('idle'); setCodeInput(''); }}
                    disabled={phoneStep === 'verifying'}
                  >
                    {t('profileSettings.phone.cancel')}
                  </GhostButton>
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* ── Language Preference ── */}
      <LanguageSettingsCard />

      {/* ── Coach Voice / AI Message Tone ──────────────────────────
           Drives the AI Message Composer. Coach picks once; every
           future AI-generated message uses that voice. Selecting
           saves immediately to users/{uid}.messageTone (no Save
           button) — independent of the surrounding form's save flow. */}
      <SectionCard
        title={t('profileSettings.coachVoice.title')}
        icon={SparklesIcon}
        description={t('profileSettings.coachVoice.desc')}
      >
        <MessageToneSelector uid={firebaseUser?.uid} />
      </SectionCard>

      {/* ── Account ── */}
      <SectionCard title={t('profileSettings.account.title')} icon={KeyIcon} description={t('profileSettings.account.desc')}>
        <div className="space-y-5">
          <Field disabled>
            <CatalystLabel className="mb-1.5 block text-sm font-medium text-zinc-700">
              {t('profileSettings.account.emailLabel')}
            </CatalystLabel>
            <input
              readOnly
              disabled
              value={firebaseUser?.email || ''}
              aria-readonly
              className={INPUT_CLASSES}
            />
            <Description className="mt-1">{t('profileSettings.account.emailHint')}</Description>
          </Field>

          <div className="border-t border-zinc-100 pt-5">
            <p className="text-sm font-medium text-zinc-700 mb-3">{t('profileSettings.account.password')}</p>
            <GhostButton
              type="button"
              onClick={handlePasswordReset}
              className="w-full sm:w-auto"
            >
              {t('profileSettings.account.sendReset')}
            </GhostButton>
            <p className="mt-2 text-xs text-zinc-400">
              {t('profileSettings.account.resetHintPrefix')}<strong>{firebaseUser?.email}</strong>
            </p>
          </div>
        </div>
      </SectionCard>

      {/* ── Danger Zone — required by Apple Guideline 5.1.1(v),
            Google Play Data Deletion policy, GDPR Art. 17, CCPA. ── */}
      <div className="bg-white rounded-2xl border border-red-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
        <div className="border-b border-red-100 px-6 py-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-50 ring-1 ring-red-200 text-red-600">
            <ExclamationTriangleIcon className="w-5 h-5" />
          </div>
          <div>
            <h3
              className="text-base font-bold uppercase tracking-wide text-red-700"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('profileSettings.danger.title')}
            </h3>
            <p className="text-xs text-zinc-500">{t('profileSettings.danger.subtitle')}</p>
          </div>
        </div>
        <div className="p-6">
          {!deleteOpen ? (
            <div className="space-y-3">
              <p className="text-sm text-zinc-700 leading-relaxed">
                {t('profileSettings.danger.intro')}
              </p>
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-white px-4 py-2 text-sm font-bold uppercase tracking-wide text-red-600 hover:bg-red-50 transition-colors"
                style={{ fontFamily: HEAD_FONT }}
              >
                <TrashIcon className="h-4 w-4" />
                {t('profileSettings.danger.deleteBtn')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 leading-relaxed">
                <p className="font-bold mb-1.5">{t('profileSettings.danger.permanent')}</p>
                <ul className="list-disc pl-5 space-y-1 text-[13px]">
                  <li>{t('profileSettings.danger.bullet1')}</li>
                  <li>{t('profileSettings.danger.bullet2')}</li>
                  <li>{t('profileSettings.danger.bullet3')}</li>
                  <li>{t('profileSettings.danger.bullet4')}</li>
                  <li>{t('profileSettings.danger.bullet5')}</li>
                  <li>{t('profileSettings.danger.bullet6Prefix')}<a href="mailto:hello@getscrimly.com" className="underline font-semibold">hello@getscrimly.com</a>{t('profileSettings.danger.bullet6Suffix')}</li>
                </ul>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-red-700"
                       style={{ fontFamily: HEAD_FONT }}>
                  {t('profileSettings.danger.confirmLabel', { token: 'DELETE-MY-ACCOUNT' })}
                </label>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="DELETE-MY-ACCOUNT"
                  autoComplete="off"
                  spellCheck={false}
                  className={clsx(INPUT_CLASSES, 'font-mono uppercase tracking-wider')}
                  disabled={deleting}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirm !== 'DELETE-MY-ACCOUNT'}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-bold uppercase tracking-wide text-white shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {deleting ? (
                    <span className="flex items-center gap-2">
                      <ArrowPathIcon className="animate-spin w-4 h-4" /> {t('profileSettings.danger.deleting')}
                    </span>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      {t('profileSettings.danger.permanentlyDelete')}
                    </>
                  )}
                </button>
                <GhostButton
                  type="button"
                  onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); }}
                  disabled={deleting}
                >
                  {t('profileSettings.danger.cancel')}
                </GhostButton>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
